import { Children } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const TEAL = "#0f7c78";

const Flow = ({ text }) => {
  const steps = text.split(/->|→/).map((s) => s.trim()).filter(Boolean);
  return (
    <figure data-testid="blog-flow" className="my-8 flex flex-col items-stretch gap-2 md:flex-row md:items-center">
      {steps.map((s, i) => (
        <div key={i} className="flex flex-col items-center gap-2 md:flex-1 md:flex-row">
          <div className="w-full rounded-xl border-2 bg-white px-4 py-3 text-center text-[15px] font-semibold leading-snug text-[#17201b]" style={{ borderColor: i === steps.length - 1 ? "#e8703a" : TEAL }}>
            <span className="mb-1 block text-[12px] font-bold tracking-[0.12em]" style={{ color: TEAL }}>STEP {i + 1}</span>{s}
          </div>
          {i < steps.length - 1 && <span className="text-[20px] font-bold md:px-1" style={{ color: TEAL }} aria-hidden="true"><span className="md:hidden">↓</span><span className="hidden md:inline">→</span></span>}
        </div>
      ))}
    </figure>
  );
};

const Timeline = ({ text }) => (
  <figure data-testid="blog-timeline" className="my-8 border-l-2 pl-6" style={{ borderColor: TEAL }}>
    {text.split("\n").map((l) => l.split("|").map((x) => x.trim())).filter((r) => r[0]).map(([when, what], i) => (
      <div key={i} className="relative pb-5 last:pb-0">
        <span className="absolute -left-[31px] top-1.5 h-3 w-3 rounded-full border-2 bg-[#f6f4ee]" style={{ borderColor: TEAL }} />
        <p className="text-[13px] font-bold tracking-[0.08em]" style={{ color: TEAL }}>{when}</p>
        <p className="text-[16px] leading-relaxed text-[#26302b]">{what}</p>
      </div>
    ))}
  </figure>
);

const TONES = { "Definition:": ["#eef6f5", TEAL], "Key idea:": ["#fff4ec", "#e8703a"], "Example:": ["#f2f5fb", "#3b5ba5"], "Remember:": ["#fbf7e9", "#a7801f"] };

const textOf = (n) => (typeof n === "string" || typeof n === "number" ? String(n) : Array.isArray(n) ? n.map(textOf).join("") : n?.props ? textOf(n.props.children) : "");

const Callout = ({ children }) => {
  const flat = textOf(Children.toArray(children)).slice(0, 80);
  const key = Object.keys(TONES).find((k) => flat.includes(k)) || "Key idea:";
  const [bg, bd] = TONES[key];
  return <aside data-testid="blog-callout" className="my-7 rounded-r-xl border-l-4 px-6 py-4 text-[16.5px] leading-relaxed text-[#26302b] [&_p]:my-1.5" style={{ background: bg, borderColor: bd }}>{children}</aside>;
};

const C = {
  h2: ({ children }) => <h2 className="mb-4 mt-14 scroll-mt-24 font-['Georgia'] text-[28px] font-normal leading-tight text-[#17201b] md:text-[32px]">{children}</h2>,
  h3: ({ children }) => <h3 className="mb-3 mt-9 font-['Georgia'] text-[21px] italic text-[#17201b]">{children}</h3>,
  p: ({ children }) => <p className="my-5 text-[17px] leading-[1.85] text-[#26302b] md:text-[18px]">{children}</p>,
  ul: ({ children }) => <ul className="my-5 list-disc space-y-2 pl-6 text-[17px] leading-[1.75] text-[#26302b] marker:text-[#0f7c78]">{children}</ul>,
  ol: ({ children }) => <ol className="my-5 list-decimal space-y-2 pl-6 text-[17px] leading-[1.75] text-[#26302b] marker:font-bold marker:text-[#0f7c78]">{children}</ol>,
  blockquote: Callout,
  strong: ({ children }) => <strong className="font-bold text-[#17201b]">{children}</strong>,
  em: ({ children }) => <em className="text-[#4c5a53]">{children}</em>,
  a: ({ href, children }) => <a href={href} className="font-semibold text-[#0f7c78] underline underline-offset-2">{children}</a>,
  table: ({ children }) => <div className="my-8 overflow-x-auto rounded-xl border border-[#d9ddd3] bg-white"><table className="w-full min-w-[520px] text-left text-[15px]">{children}</table></div>,
  th: ({ children }) => <th className="bg-[#17201b] px-4 py-3 text-[13px] font-bold uppercase tracking-[0.06em] text-white">{children}</th>,
  td: ({ children }) => <td className="border-t border-[#e4e6df] px-4 py-3 align-top leading-relaxed text-[#26302b]">{children}</td>,
  hr: () => <hr className="my-12 border-[#d9ddd3]" />,
  pre: ({ children }) => <>{children}</>,
  code: ({ className, children }) => {
    const text = String(children).trim();
    if (className?.includes("language-flow")) return <Flow text={text} />;
    if (className?.includes("language-timeline")) return <Timeline text={text} />;
    return className ? <pre className="my-6 overflow-x-auto rounded-xl bg-[#17201b] p-5 text-[14px] text-[#e9efe9]"><code>{text}</code></pre>
      : <code className="rounded bg-[#e9ece4] px-1.5 py-0.5 text-[15px]">{children}</code>;
  },
};

export const MarkdownBody = ({ body }) => (
  <div data-testid="blog-body"><ReactMarkdown remarkPlugins={[remarkGfm]} components={C}>{body || ""}</ReactMarkdown></div>
);
